import { api } from '../../api';

// ─────────────────────────────────────────────
// Types & Interfaces
// ─────────────────────────────────────────────

export const CommissionChangeType = {
  COMMISSION_ASSIGNED: 'COMMISSION_ASSIGNED',
  RULE_APPLIED: 'RULE_APPLIED',
  RULE_FAILED: 'RULE_FAILED',
  RULE_REVOKED: 'RULE_REVOKED',
} as const;

export type CommissionChangeType = (typeof CommissionChangeType)[keyof typeof CommissionChangeType];

export const NotMetActionType = {
  KEEP_CURRENT: 'keep_current',
  REDUCE: 'reduce',
} as const;

export type NotMetActionType = (typeof NotMetActionType)[keyof typeof NotMetActionType];

export const ProgramScopeType = {
  ALL: 'all',
  SELECTED: 'selected',
} as const;

export type ProgramScopeType = (typeof ProgramScopeType)[keyof typeof ProgramScopeType];

export const RuleType = {
  GROUP_FILL: 'group_fill',
  STUDENT_COUNT: 'student_count',
} as const;

export type RuleType = (typeof RuleType)[keyof typeof RuleType];

export interface AgentScope {
  allAgents: boolean;
  branchIds: string[];
  trackIds: string[];
  bundleIds: string[];
}

export interface ProgramScope {
  type: ProgramScopeType;
  programIds: string[];
}

export interface TargetMetAction {
  commissionValue: number;
}

export interface TargetNotMetAction {
  type: NotMetActionType;
  commissionValue?: number;
}

export interface CommissionRule {
  _id: string;
  name: string;
  description?: string;
  ruleType?: RuleType;
  targetFillPercentage?: number;
  targetStudentCount?: number;
  targetDate?: string;
  targetMetAction: TargetMetAction;
  targetNotMetAction: TargetNotMetAction;
  agentScope: AgentScope;
  programScope: ProgramScope;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentCommission {
  salesRoleId: string;
  commission: number | null;
  appliedRule: { _id: string; ruleName: string; isMet: boolean } | null;
  effectiveFrom: string | null;
}

export interface CommissionHistoryEntry {
  _id: string;
  salesRoleId: string;
  changeType: CommissionChangeType;
  oldValue: number | null;
  newValue: number;
  source: {
    type: 'manual' | 'rule';
    ruleId?: { _id: string; name: string } | string;
  };
  changedBy: { _id: string; name?: string; email?: string } | string;
  notes?: string;
  createdAt: string;
}

export interface SetCommissionPayload {
  commission: number;
  effectiveFrom?: string;
  notes?: string;
}

export interface CreateCommissionRulePayload {
  name: string;
  description?: string;
  ruleType?: RuleType;
  targetFillPercentage?: number;
  targetStudentCount?: number;
  targetDate?: string;
  targetMetAction: TargetMetAction;
  targetNotMetAction: TargetNotMetAction;
  agentScope?: Partial<AgentScope>;
  programScope: ProgramScope;
}

export interface UpdateCommissionRulePayload extends Partial<CreateCommissionRulePayload> {
  isActive?: boolean;
}

export interface RulesQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface RuleProgressItem {
  ruleId: string;
  ruleName: string;
  description?: string;
  ruleType?: RuleType;
  // group_fill fields
  targetFillPercentage?: number;
  currentFillPercentage?: number;
  groupCount?: number;
  totalSlots?: number;
  filledSlots?: number;
  // student_count fields
  targetStudentCount?: number;
  currentStudentCount?: number;
  // shared fields
  targetDate?: string;
  status: 'met' | 'not_met' | 'in_progress';
  targetMetAction: TargetMetAction;
  targetNotMetAction: TargetNotMetAction;
  isEvaluated: boolean;
}

export interface AgentEarnings {
  totalCommissionEarned: number;
  leadCommissionEarned: number;
  totalBills: number;
  leadBills: number;
  totalRevenue: number;
  leadRevenue: number;
}

export interface AgentProgressData {
  salesRoleId: string;
  commission: {
    commission: number;
    appliedRule: { _id: string; name: string } | null;
    effectiveFrom: string;
  } | null;
  earnings: AgentEarnings;
  ruleProgress: RuleProgressItem[];
  needsEvaluation: boolean;
}

export interface AgentProgressSummary {
  salesRoleId: string;
  agentName: string;
  agentEmail: string;
  commission: number | null;
  totalCommissionEarned: number;
  leadCommissionEarned: number;
  totalBills: number;
  leadBills: number;
  totalRevenue: number;
  needsEvaluation: boolean;
}

// ─────────────────────────────────────────────
// Commission Service
// ─────────────────────────────────────────────

export const CommissionService = {
  /** Get current commission for an agent */
  getAgentCommission: async (salesId: string): Promise<AgentCommission> => {
    const response = await api.get<{ success: boolean; data: AgentCommission }>(
      `/sales/agents/${salesId}/commission`
    );
    return response.data.data;
  },

  /** Set or update commission for an agent manually */
  setCommission: async (
    salesId: string,
    payload: SetCommissionPayload
  ): Promise<AgentCommission> => {
    const response = await api.put<{ success: boolean; data: AgentCommission }>(
      `/sales/agents/${salesId}/commission`,
      payload
    );
    return response.data.data;
  },

  /** Get paginated commission history for an agent */
  getHistory: async (
    salesId: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<CommissionHistoryEntry>> => {
    const response = await api.get<{
      success: boolean;
      data: CommissionHistoryEntry[];
      pagination: PaginatedResponse<CommissionHistoryEntry>['pagination'];
    }>(`/sales/agents/${salesId}/commission/history`, { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  /** Manually trigger rule evaluation for an agent */
  evaluateAgent: async (salesId: string): Promise<{ commission: number }> => {
    const response = await api.post<{
      success: boolean;
      data: { commission: number };
    }>(`/sales/agents/${salesId}/commission/evaluate`);
    return response.data.data;
  },

  // ── Rules ───────────────────────────────────

  getRules: async (params?: RulesQueryParams): Promise<PaginatedResponse<CommissionRule>> => {
    const response = await api.get<{
      success: boolean;
      data: CommissionRule[];
      pagination: PaginatedResponse<CommissionRule>['pagination'];
    }>('/sales/commission/rules', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },

  getRuleById: async (ruleId: string): Promise<CommissionRule> => {
    const response = await api.get<{ success: boolean; data: CommissionRule }>(
      `/sales/commission/rules/${ruleId}`
    );
    return response.data.data;
  },

  createRule: async (payload: CreateCommissionRulePayload): Promise<CommissionRule> => {
    const response = await api.post<{ success: boolean; data: CommissionRule }>(
      '/sales/commission/rules',
      payload
    );
    return response.data.data;
  },

  updateRule: async (
    ruleId: string,
    payload: UpdateCommissionRulePayload
  ): Promise<CommissionRule> => {
    const response = await api.put<{ success: boolean; data: CommissionRule }>(
      `/sales/commission/rules/${ruleId}`,
      payload
    );
    return response.data.data;
  },

  deleteRule: async (ruleId: string): Promise<void> => {
    await api.delete(`/sales/commission/rules/${ruleId}`);
  },

  /** Bulk evaluate all agents */
  evaluateAll: async (): Promise<{ processed: number; errors: number }> => {
    const response = await api.post<{
      success: boolean;
      data: { processed: number; errors: number };
    }>('/sales/commission/evaluate-all');
    return response.data.data;
  },

  // ── Agent Progress ─────────────────────────

  /** Get detailed progress for a single agent */
  getAgentProgress: async (salesId: string): Promise<AgentProgressData> => {
    const response = await api.get<{ success: boolean; data: AgentProgressData }>(
      `/sales/agents/${salesId}/commission/progress`
    );
    return response.data.data;
  },

  /** Get paginated progress summary for all agents */
  getAgentsProgress: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PaginatedResponse<AgentProgressSummary>> => {
    const response = await api.get<{
      success: boolean;
      data: AgentProgressSummary[];
      pagination: PaginatedResponse<AgentProgressSummary>['pagination'];
    }>('/sales/commission/agents-progress', { params });
    return { data: response.data.data, pagination: response.data.pagination };
  },
};
